<template>
    <Teleport to="body">
      <Transition name="tutorial-fade">
        <div
          v-if="visible && targetRect"
          class="tutorial-root"
        >
          <!--
            Невидимые блокеры.
            Они не дают нажимать на Feed,
            но область вокруг + остаётся кликабельной.
          -->
          <div
            class="interaction-blocker blocker-top"
            :style="topBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-left"
            :style="leftBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-right"
            :style="rightBlockerStyle"
          />
  
          <div
            class="interaction-blocker blocker-bottom"
            :style="bottomBlockerStyle"
          />
  
          <!-- Затемнение с дыркой -->
          <div
            class="spotlight"
            :style="spotlightStyle"
          ></div>
  
          <!-- Красивая стрелка -->
          <svg
            v-if="arrowPath"
            class="tutorial-arrow-layer"
            width="100%"
            height="100%"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="tutorialArrowGradient"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop
                  offset="0%"
                  stop-color="#7DB6FF"
                />
  
                <stop
                  offset="100%"
                  stop-color="#2887F7"
                />
              </linearGradient>
  
              <marker
                id="tutorialArrowHead"
                markerWidth="12"
                markerHeight="12"
                refX="9"
                refY="5"
                orient="auto"
                markerUnits="strokeWidth"
              >
                <path
                  d="M0,0 L10,5 L0,10 Z"
                  fill="#2887F7"
                />
              </marker>
            </defs>
  
            <path
              :d="arrowPath"
              class="tutorial-arrow-path"
              marker-end="url(#tutorialArrowHead)"
            />
          </svg>
  
          <!-- Карточка -->
          <div
            ref="calloutRef"
            class="tutorial-card"
            :class="`placement-${placement}`"
            :style="cardStyle"
          >
            <div class="tutorial-badge">
              <span class="badge-dot"></span>
              Первый шаг
            </div>
  
            <div class="tutorial-icon">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <rect
                  x="3.5"
                  y="5"
                  width="17"
                  height="15"
                  rx="3"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
  
                <path
                  d="M8 5V3.8C8 2.806 8.806 2 9.8 2H14.2C15.194 2 16 2.806 16 3.8V5"
                  stroke="currentColor"
                  stroke-width="1.8"
                />
  
                <path
                  d="M12 9V16M8.5 12.5H15.5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
            </div>
  
            <h2 class="tutorial-title">
              Создайте свою первую вакансию
            </h2>
  
            <p class="tutorial-description">
              Нажмите на
              <strong>«+»</strong>,
              чтобы опубликовать первую вакансию
              и начать получать отклики.
            </p>
  
            <div class="free-vacancy-card">
              <div class="free-vacancy-icon">
                🎁
              </div>
  
              <div>
                <span class="free-vacancy-title">
                  Первая вакансия — бесплатно
                </span>
  
                <span class="free-vacancy-text">
                  Попробуйте размещение без подписки.
                </span>
              </div>
            </div>
  
            <div class="premium-hint">
              <div class="premium-icon">
                ✦
              </div>
  
              <p>
                Для следующих размещений и расширенных
                возможностей доступен
                <strong>Premium</strong>.
              </p>
            </div>
  
            <div class="tutorial-actions">
              <button
                type="button"
                class="later-button"
                @click="closeTutorial"
              >
                Позже
              </button>
  
              <button
                type="button"
                class="create-button"
                @click="triggerCreateVacancy"
              >
                Создать вакансию
  
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M9 6L15 12L9 18"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>
  
            <p class="tutorial-footnote">
              Или просто нажмите на подсвеченный
              <strong>+</strong>
            </p>
          </div>
        </div>
      </Transition>
    </Teleport>
  </template>
  
  <script setup>
  import {
    computed,
    nextTick,
    onBeforeUnmount,
    ref,
    watch,
  } from "vue";

  import UserService from "../api/UserService.js";

  let tutorialSeenWasSent = false;

  const markTutorialAsSeen = async () => {
  if (tutorialSeenWasSent) {
    return;
  }

  tutorialSeenWasSent = true;

  try {
    await UserService.markFirstTutorialAsSeen();

    console.log(
      "✅ Первый tutorial отмечен как просмотренный"
    );
  } catch (error) {
    /*
     * Если сеть отвалилась — не мешаем пользователю
     * продолжить работу.
     *
     * Разрешаем повторную попытку, если компонент
     * ещё останется жить.
     */
    tutorialSeenWasSent = false;

    console.error(
      "❌ Не удалось сохранить просмотр tutorial:",
      error
    );
  }
};

  const props = defineProps({
    visible: {
      type: Boolean,
      default: false,
    },
  
    targetElement: {
      type: Object,
      default: null,
    },
  
    targetSelector: {
      type: String,
      default: "",
    },
  });
  
  const emit = defineEmits([
    "close",
    "create",
  ]);
  
  const SPOTLIGHT_PADDING = 12;
  const CARD_GAP = 52;
  
  const targetRect = ref(null);
  const calloutRef = ref(null);
  
  const placement = ref("left");
  const cardPosition = ref({
    top: 0,
    left: 0,
  });
  
  const arrowPath = ref("");
  
  let activeTarget = null;
  let frameId = null;
  
  // MARK: - Target
  
  const resolveTarget = () => {
    if (props.targetElement) {
      return props.targetElement;
    }
  
    if (props.targetSelector) {
      return document.querySelector(
        props.targetSelector
      );
    }
  
    return null;
  };
  
  const updatePosition = async () => {
    if (!props.visible) {
      return;
    }
  
    const element = resolveTarget();
  
    if (!element) {
      targetRect.value = null;
      return;
    }
  
    activeTarget = element;
  
    const rect =
      element.getBoundingClientRect();
  
    const paddedRect = {
      top:
        rect.top -
        SPOTLIGHT_PADDING,
  
      left:
        rect.left -
        SPOTLIGHT_PADDING,
  
      right:
        rect.right +
        SPOTLIGHT_PADDING,
  
      bottom:
        rect.bottom +
        SPOTLIGHT_PADDING,
  
      width:
        rect.width +
        SPOTLIGHT_PADDING * 2,
  
      height:
        rect.height +
        SPOTLIGHT_PADDING * 2,
    };
  
    targetRect.value = paddedRect;
  
    await nextTick();
  
    calculateCardPosition();
  };
  
  const scheduleUpdate = () => {
    if (frameId) {
      cancelAnimationFrame(frameId);
    }
  
    frameId =
      requestAnimationFrame(
        updatePosition
      );
  };
  
  // MARK: - Card
  
  const calculateCardPosition = async () => {
    if (
      !targetRect.value ||
      !calloutRef.value
    ) {
      return;
    }
  
    await nextTick();
  
    const target =
      targetRect.value;
  
    const cardRect =
      calloutRef.value.getBoundingClientRect();
  
    const viewportWidth =
      window.innerWidth;
  
    const viewportHeight =
      window.innerHeight;
  
    const margin = 20;
  
    const targetCenterX =
      target.left +
      target.width / 2;
  
    /*
     * На десктопе + находится справа,
     * поэтому карточка почти всегда будет слева.
     */
    const spaceLeft =
      target.left;
  
    const spaceRight =
      viewportWidth -
      target.right;
  
    let nextPlacement;
  
    if (
      spaceLeft >
      cardRect.width +
        CARD_GAP +
        margin
    ) {
      nextPlacement = "left";
    } else if (
      spaceRight >
      cardRect.width +
        CARD_GAP +
        margin
    ) {
      nextPlacement = "right";
    } else {
      nextPlacement =
        target.top >
        viewportHeight / 2
          ? "top"
          : "bottom";
    }
  
    placement.value =
      nextPlacement;
  
    let top = 0;
    let left = 0;
  
    if (nextPlacement === "left") {
      left =
        target.left -
        cardRect.width -
        CARD_GAP;
  
      top =
        target.top +
        target.height / 2 -
        cardRect.height / 2;
    }
  
    if (nextPlacement === "right") {
      left =
        target.right +
        CARD_GAP;
  
      top =
        target.top +
        target.height / 2 -
        cardRect.height / 2;
    }
  
    if (nextPlacement === "top") {
      top =
        target.top -
        cardRect.height -
        CARD_GAP;
  
      left =
        targetCenterX -
        cardRect.width / 2;
    }
  
    if (nextPlacement === "bottom") {
      top =
        target.bottom +
        CARD_GAP;
  
      left =
        targetCenterX -
        cardRect.width / 2;
    }
  
    left = Math.max(
      margin,
      Math.min(
        left,
        viewportWidth -
          cardRect.width -
          margin
      )
    );
  
    top = Math.max(
      margin,
      Math.min(
        top,
        viewportHeight -
          cardRect.height -
          margin
      )
    );
  
    cardPosition.value = {
      top,
      left,
    };
  
    await nextTick();
  
    calculateArrow();
  };
  
  // MARK: - Arrow
  
  const calculateArrow = () => {
    if (
      !targetRect.value ||
      !calloutRef.value
    ) {
      arrowPath.value = "";
      return;
    }
  
    const target =
      targetRect.value;
  
    const card =
      calloutRef.value.getBoundingClientRect();
  
    const endX =
      target.left +
      target.width / 2;
  
    const endY =
      target.top +
      target.height / 2;
  
    let startX;
    let startY;
    let path;
  
    if (placement.value === "left") {
      startX = card.right - 4;
      startY =
        card.top +
        card.height * 0.45;
  
      const distance =
        Math.abs(
          endX -
          startX
        );
  
      path = `
        M ${startX} ${startY}
        C
        ${startX + distance * 0.45}
        ${startY - 22},
  
        ${endX - distance * 0.35}
        ${endY + 24},
  
        ${endX}
        ${endY}
      `;
    } else if (
      placement.value === "right"
    ) {
      startX = card.left + 4;
      startY =
        card.top +
        card.height * 0.45;
  
      const distance =
        Math.abs(
          startX -
          endX
        );
  
      path = `
        M ${startX} ${startY}
        C
        ${startX - distance * 0.45}
        ${startY - 22},
  
        ${endX + distance * 0.35}
        ${endY + 24},
  
        ${endX}
        ${endY}
      `;
    } else if (
      placement.value === "top"
    ) {
      startX =
        card.left +
        card.width * 0.62;
  
      startY =
        card.bottom - 4;
  
      const distance =
        Math.abs(
          endY -
          startY
        );
  
      path = `
        M ${startX} ${startY}
        C
        ${startX + 25}
        ${startY + distance * 0.35},
  
        ${endX - 25}
        ${endY - distance * 0.35},
  
        ${endX}
        ${endY}
      `;
    } else {
      startX =
        card.left +
        card.width * 0.62;
  
      startY =
        card.top + 4;
  
      const distance =
        Math.abs(
          startY -
          endY
        );
  
      path = `
        M ${startX} ${startY}
        C
        ${startX + 25}
        ${startY - distance * 0.35},
  
        ${endX - 25}
        ${endY + distance * 0.35},
  
        ${endX}
        ${endY}
      `;
    }
  
    arrowPath.value =
      path.replace(/\s+/g, " ");
  };
  
  // MARK: - Styles
  
  const spotlightStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      top:
        `${targetRect.value.top}px`,
  
      left:
        `${targetRect.value.left}px`,
  
      width:
        `${targetRect.value.width}px`,
  
      height:
        `${targetRect.value.height}px`,
    };
  });
  
  const cardStyle = computed(() => ({
    top:
      `${cardPosition.value.top}px`,
  
    left:
      `${cardPosition.value.left}px`,
  }));
  
  const topBlockerStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      height:
        `${Math.max(
          0,
          targetRect.value.top
        )}px`,
    };
  });
  
  const bottomBlockerStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      top:
        `${targetRect.value.bottom}px`,
    };
  });
  
  const leftBlockerStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      top:
        `${targetRect.value.top}px`,
  
      width:
        `${Math.max(
          0,
          targetRect.value.left
        )}px`,
  
      height:
        `${targetRect.value.height}px`,
    };
  });
  
  const rightBlockerStyle = computed(() => {
    if (!targetRect.value) {
      return {};
    }
  
    return {
      top:
        `${targetRect.value.top}px`,
  
      left:
        `${targetRect.value.right}px`,
  
      height:
        `${targetRect.value.height}px`,
    };
  });
  
  // MARK: - Actions
  
// MARK: - Actions

const closeTutorial = async () => {
  /*
   * Нажали "Позже".
   * Считаем tutorial просмотренным.
   */
  await markTutorialAsSeen();

  emit("close");
};

const triggerCreateVacancy = async () => {
  /*
   * Нажали большую кнопку
   * "Создать вакансию".
   */
  await markTutorialAsSeen();

  /*
   * Сначала закрываем tutorial,
   * чтобы listener на реальной + не вызвал
   * повторные события.
   */
  detachTargetListener();

  emit("create");
  emit("close");

  await nextTick();

  const element = resolveTarget();

  /*
   * Нажимаем настоящую кнопку +.
   * Поэтому продолжает работать твоя
   * существующая handleCreateVacancy().
   */
  element?.click();
};

const handleTargetClick = async () => {
  /*
   * Пользователь нажал прямо
   * на подсвеченную настоящую кнопку +.
   */
  await markTutorialAsSeen();

  emit("create");
  emit("close");
};
  
  // MARK: - Listener management
  
  const attachTargetListener = () => {
    detachTargetListener();
  
    activeTarget =
      resolveTarget();
  
    activeTarget?.addEventListener(
      "click",
      handleTargetClick
    );
  };
  
  const detachTargetListener = () => {
    activeTarget?.removeEventListener(
      "click",
      handleTargetClick
    );
  
    activeTarget = null;
  };
  
  watch(
    [
      () => props.visible,
      () => props.targetElement,
    ],
    async ([visible]) => {
      if (!visible) {
        detachTargetListener();
        targetRect.value = null;
        arrowPath.value = "";
        return;
      }
  
      await nextTick();
  
      await updatePosition();
  
      attachTargetListener();
    },
    {
      immediate: true,
      flush: "post",
    }
  );
  
  window.addEventListener(
    "resize",
    scheduleUpdate,
    {
      passive: true,
    }
  );
  
  /*
   * true нужен потому что Feed скроллится
   * внутри .feed-content, а не window.
   */
  window.addEventListener(
    "scroll",
    scheduleUpdate,
    true
  );
  
  onBeforeUnmount(() => {
    detachTargetListener();
  
    window.removeEventListener(
      "resize",
      scheduleUpdate
    );
  
    window.removeEventListener(
      "scroll",
      scheduleUpdate,
      true
    );
  
    if (frameId) {
      cancelAnimationFrame(frameId);
    }
  });
  </script>
  
  <style scoped>
  .tutorial-root {
    position: fixed;
    inset: 0;
  
    z-index: 1000000;
  
    pointer-events: none;
  }
  
  /* Interaction blockers */
  
  .interaction-blocker {
    position: fixed;
  
    z-index: 1;
  
    pointer-events: auto;
  }
  
  .blocker-top {
    top: 0;
    left: 0;
    right: 0;
  }
  
  .blocker-bottom {
    left: 0;
    right: 0;
    bottom: 0;
  }
  
  .blocker-left {
    left: 0;
  }
  
  .blocker-right {
    right: 0;
  }
  
  /* Spotlight */
  
  .spotlight {
    position: fixed;
  
    z-index: 2;
  
    border:
      2px solid
      rgba(91, 164, 255, 0.95);
  
    border-radius: 22px;
  
    pointer-events: none;
  
    box-shadow:
      0 0 0 9999px
        rgba(5, 10, 20, 0.78),
  
      0 0 0 5px
        rgba(40, 135, 247, 0.13),
  
      0 0 34px
        rgba(40, 135, 247, 0.8),
  
      inset 0 0 20px
        rgba(40, 135, 247, 0.14);
  
    animation:
      spotlightPulse
      2s
      ease-in-out
      infinite;
  }
  
  @keyframes spotlightPulse {
    0%,
    100% {
      box-shadow:
        0 0 0 9999px
          rgba(5, 10, 20, 0.78),
  
        0 0 0 5px
          rgba(40, 135, 247, 0.12),
  
        0 0 26px
          rgba(40, 135, 247, 0.55);
    }
  
    50% {
      box-shadow:
        0 0 0 9999px
          rgba(5, 10, 20, 0.78),
  
        0 0 0 8px
          rgba(40, 135, 247, 0.09),
  
        0 0 48px
          rgba(40, 135, 247, 0.9);
    }
  }
  
  /* Arrow */
  
  .tutorial-arrow-layer {
    position: fixed;
    inset: 0;
  
    z-index: 3;
  
    overflow: visible;
  
    pointer-events: none;
  }
  
  .tutorial-arrow-path {
    fill: none;
  
    stroke:
      url(#tutorialArrowGradient);
  
    stroke-width: 3;
  
    stroke-linecap: round;
  
    stroke-dasharray: 8 7;
  
    filter:
      drop-shadow(
        0 4px 8px
        rgba(40, 135, 247, 0.35)
      );
  
    animation:
      arrowFlow
      1.4s
      linear
      infinite;
  }
  
  @keyframes arrowFlow {
    to {
      stroke-dashoffset: -30;
    }
  }
  
  /* Card */
  
  .tutorial-card {
    width: min(
      390px,
      calc(100vw - 32px)
    );
  
    position: fixed;
  
    z-index: 4;
  
    padding:
      26px;
  
    box-sizing: border-box;
  
    color:
      rgba(255, 255, 255, 0.96);
  
    background:
      linear-gradient(
        145deg,
        rgba(29, 36, 49, 0.98),
        rgba(17, 22, 31, 0.98)
      );
  
    border:
      1px solid
      rgba(255, 255, 255, 0.12);
  
    border-radius: 26px;
  
    box-shadow:
      0 35px 90px
        rgba(0, 0, 0, 0.48),
  
      0 10px 35px
        rgba(0, 0, 0, 0.25),
  
      inset 0 1px 0
        rgba(255, 255, 255, 0.06);
  
    pointer-events: auto;
  
    animation:
      tutorialCardAppear
      0.45s
      cubic-bezier(
        0.22,
        1,
        0.36,
        1
      );
  }
  
  .tutorial-card::before {
    content: "";
  
    position: absolute;
  
    width: 220px;
    height: 220px;
  
    top: -150px;
    right: -100px;
  
    background:
      radial-gradient(
        circle,
        rgba(40, 135, 247, 0.22),
        transparent 70%
      );
  
    pointer-events: none;
  }
  
  @keyframes tutorialCardAppear {
    from {
      opacity: 0;
  
      transform:
        translateY(10px)
        scale(0.96);
    }
  
    to {
      opacity: 1;
  
      transform:
        translateY(0)
        scale(1);
    }
  }
  
  /* Badge */
  
  .tutorial-badge {
    width: fit-content;
  
    display: inline-flex;
    align-items: center;
  
    gap: 7px;
  
    margin-bottom: 18px;
  
    padding:
      6px
      10px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 10px;
  
    color:
      #93c4ff;
  
    background:
      rgba(40, 135, 247, 0.1);
  
    border:
      1px solid
      rgba(40, 135, 247, 0.19);
  
    border-radius: 999px;
  
    text-transform: uppercase;
  
    letter-spacing: 0.5px;
  }
  
  .badge-dot {
    width: 6px;
    height: 6px;
  
    background:
      #54a0ff;
  
    border-radius: 50%;
  
    box-shadow:
      0 0 10px
      rgba(84, 160, 255, 0.8);
  }
  
  /* Icon */
  
  .tutorial-icon {
    width: 54px;
    height: 54px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    margin-bottom: 17px;
  
    color:
      #77b5ff;
  
    background:
      linear-gradient(
        145deg,
        rgba(40, 135, 247, 0.2),
        rgba(40, 135, 247, 0.07)
      );
  
    border:
      1px solid
      rgba(40, 135, 247, 0.2);
  
    border-radius: 17px;
  
    box-shadow:
      0 10px 30px
        rgba(40, 135, 247, 0.14);
  }
  
  .tutorial-icon svg {
    width: 28px;
    height: 28px;
  }
  
  /* Copy */
  
  .tutorial-title {
    margin:
      0 0
      10px;
  
    font-family:
      "Eastman-Bold",
      sans-serif;
  
    font-size: 24px;
    line-height: 1.2;
  
    letter-spacing:
      -0.45px;
  }
  
  .tutorial-description {
    margin:
      0 0
      20px;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 13px;
    line-height: 1.6;
  
    color:
      rgba(255, 255, 255, 0.62);
  }
  
  .tutorial-description strong {
    color:
      rgba(255, 255, 255, 0.96);
  }
  
  /* Free vacancy */
  
  .free-vacancy-card {
    display: flex;
    align-items: center;
  
    gap: 12px;
  
    padding:
      13px
      14px;
  
    margin-bottom: 12px;
  
    background:
      linear-gradient(
        135deg,
        rgba(42, 181, 111, 0.13),
        rgba(42, 181, 111, 0.05)
      );
  
    border:
      1px solid
      rgba(62, 211, 133, 0.17);
  
    border-radius: 16px;
  }
  
  .free-vacancy-icon {
    width: 38px;
    height: 38px;
  
    flex: 0 0 38px;
  
    display: flex;
    align-items: center;
    justify-content: center;
  
    background:
      rgba(50, 205, 125, 0.12);
  
    border-radius: 12px;
  
    font-size: 18px;
  }
  
  .free-vacancy-title,
  .free-vacancy-text {
    display: block;
  }
  
  .free-vacancy-title {
    margin-bottom: 2px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 12px;
  
    color:
      #8ce6b6;
  }
  
  .free-vacancy-text {
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 10px;
    line-height: 1.4;
  
    color:
      rgba(255, 255, 255, 0.48);
  }
  
  /* Premium */
  
  .premium-hint {
    display: flex;
    align-items: flex-start;
  
    gap: 9px;
  
    margin:
      0 0
      22px;
  
    padding:
      11px
      12px;
  
    background:
      rgba(255, 255, 255, 0.035);
  
    border:
      1px solid
      rgba(255, 255, 255, 0.07);
  
    border-radius: 14px;
  }
  
  .premium-icon {
    color:
      #b993ff;
  
    font-size: 14px;
  }
  
  .premium-hint p {
    margin: 0;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 10px;
    line-height: 1.5;
  
    color:
      rgba(255, 255, 255, 0.46);
  }
  
  .premium-hint strong {
    color:
      #c5a9ff;
  }
  
  /* Actions */
  
  .tutorial-actions {
    display: grid;
  
    grid-template-columns:
      auto
      1fr;
  
    gap: 9px;
  }
  
  .later-button,
  .create-button {
    min-height: 46px;
  
    border: none;
  
    border-radius: 14px;
  
    font-family:
      "Eastman-Medium",
      sans-serif;
  
    font-size: 12px;
  
    cursor: pointer;
  
    transition:
      transform 0.18s ease,
      background 0.18s ease,
      box-shadow 0.18s ease;
  }
  
  .later-button {
    padding:
      0
      17px;
  
    color:
      rgba(255, 255, 255, 0.58);
  
    background:
      rgba(255, 255, 255, 0.06);
  
    border:
      1px solid
      rgba(255, 255, 255, 0.08);
  }
  
  .later-button:hover {
    color:
      rgba(255, 255, 255, 0.9);
  
    background:
      rgba(255, 255, 255, 0.09);
  }
  
  .create-button {
    display: flex;
    align-items: center;
    justify-content: center;
  
    gap: 8px;
  
    padding:
      0
      18px;
  
    color: white;
  
    background:
      linear-gradient(
        135deg,
        #2887f7,
        #4f46e5
      );
  
    box-shadow:
      0 12px 28px
        rgba(40, 135, 247, 0.3);
  }
  
  .create-button svg {
    width: 16px;
    height: 16px;
  
    transition:
      transform 0.18s ease;
  }
  
  .create-button:hover {
    transform:
      translateY(-1px);
  
    box-shadow:
      0 16px 34px
        rgba(40, 135, 247, 0.38);
  }
  
  .create-button:hover svg {
    transform:
      translateX(3px);
  }
  
  .tutorial-footnote {
    margin:
      12px 0
      0;
  
    text-align: center;
  
    font-family:
      "Eastman-Regular",
      sans-serif;
  
    font-size: 9px;
  
    color:
      rgba(255, 255, 255, 0.32);
  }
  
  /* Transition */
  
  .tutorial-fade-enter-active,
  .tutorial-fade-leave-active {
    transition:
      opacity 0.25s ease;
  }
  
  .tutorial-fade-enter-from,
  .tutorial-fade-leave-to {
    opacity: 0;
  }
  
  /* Mobile */
  
  @media (max-width: 768px) {
    .tutorial-card {
      width:
        calc(100vw - 30px);
  
      padding:
        21px;
  
      border-radius: 22px;
    }
  
    .tutorial-title {
      font-size: 20px;
    }
  
    .tutorial-description {
      font-size: 12px;
    }
  
    .tutorial-icon {
      width: 48px;
      height: 48px;
    }
  
    .tutorial-actions {
      grid-template-columns: 1fr;
    }
  
    .create-button {
      order: -1;
    }
  
    .tutorial-footnote {
      display: none;
    }
  }
  
  @media (
    prefers-reduced-motion:
    reduce
  ) {
    .spotlight,
    .tutorial-arrow-path,
    .tutorial-card {
      animation:
        none !important;
    }
  }
  </style>